import { type DragEvent, useEffect, useRef, useState } from "react";
import { useApi } from "@/api/useApi";
import { useCatalog, useUsableCategories } from "@/api/catalog";
import type {
  ExpenseEntry,
  ExpenseTypeDefinition,
  MileageQuote,
  ReportDetail,
  RuleMode,
} from "@/api/types";
import { TypeNotice } from "@/components/TypeNotice";
import { Icon } from "@/components/Icon";
import { findType, miles, money, todayIso } from "@/format";
import { prepareReceipt } from "@/photo";
import { ReceiptAccept, firstReceipt, named, type ReceiptVia } from "@/receiptFiles";
import { useDialogs } from "@/components/dialogs";

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

/** Whether the browser lets a button read an image or PDF from the clipboard. */
const canReadClipboard =
  typeof navigator !== "undefined" && typeof navigator.clipboard?.read === "function";

async function receiptFromClipboard(): Promise<File | undefined> {
  for (const item of await navigator.clipboard.read()) {
    const type = item.types.find((t) => t.startsWith("image/") || t === "application/pdf");
    if (type) return named(new File([await item.getType(type)], "", { type }));
  }
  return undefined;
}

/**
 * Takes a receipt pasted anywhere on the page or dropped on the element the drop handlers go on.
 * Text still pastes into whichever field has focus.
 */
function useReceiptInput(
  enabled: boolean,
  onFile: (file: File, via: ReceiptVia) => void,
  onRefused: (text: string) => void,
) {
  const [dragging, setDragging] = useState(false);
  const handlers = useRef({ onFile, onRefused });
  useEffect(() => {
    handlers.current = { onFile, onRefused };
  });

  useEffect(() => {
    if (!enabled) return;
    const onPaste = (event: ClipboardEvent) => {
      const file = firstReceipt(event.clipboardData?.files);
      if (!file) return;
      event.preventDefault();
      handlers.current.onFile(file, "Paste");
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [enabled]);

  const hasFiles = (event: DragEvent) => enabled && event.dataTransfer.types.includes("Files");
  const dropProps = {
    onDragOver: (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      setDragging(true);
    },
    onDragLeave: (event: DragEvent) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
    },
    onDrop: (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      setDragging(false);
      const file = firstReceipt(event.dataTransfer.files);
      if (file) onFile(file, "Drop");
      else onRefused("Drop a photo, a PDF or an email of the receipt.");
    },
  };
  return { dragging, dropProps };
}

/** Reads a picture or PDF off the clipboard, for phones, where there's no Ctrl+V. */
function PasteButton({
  className = "btn ghost small",
  onFile,
  onRefused,
}: {
  className?: string;
  onFile: (file: File) => void;
  onRefused: (text: string) => void;
}) {
  if (!canReadClipboard) return null;
  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        receiptFromClipboard()
          .then((file) => {
            if (file) onFile(file);
            else onRefused("There's no picture or PDF on the clipboard to paste.");
          })
          .catch(() => onRefused("The browser didn't allow pasting. Try Ctrl+V or Cmd+V."));
      }}
    >
      <Icon name="paste" size={16} /> Paste
    </button>
  );
}

function FormError({ text }: { text: string | undefined }) {
  if (!text) return null;
  return (
    <div className="error" role="alert">
      {text}
    </div>
  );
}

/**
 * Adds a typed-in expense, or corrects an existing manual or receipt entry when `entry` is set. A
 * receipt can be attached as well: the expense is saved first, then the receipt is added to it. A
 * pending entry (no report yet) is saved through its own endpoint and handed back to `onPendingSaved`.
 */
export function ExpenseForm({
  reportId,
  entry,
  knownEntryIds = [],
  canDeduct = false,
  onSaved,
  onPendingSaved,
}: {
  reportId?: string;
  entry?: ExpenseEntry;
  /** The report's entries before this one, so a new entry can be told apart after saving. */
  knownEntryIds?: string[];
  /** Listed in Settings for paycheck deductions, so the personal part can come out of their pay. */
  canDeduct?: boolean;
  onSaved?: (report: ReportDetail) => void;
  onPendingSaved?: (entry: ExpenseEntry) => void;
}) {
  const api = useApi();
  const dialogs = useDialogs();
  const catalog = useCatalog();
  const usable = useUsableCategories();
  const foreign = entry !== undefined && entry.currency !== "USD";
  const [merchant, setMerchant] = useState(
    entry && !(entry.kind === "Receipt" && entry.merchant === "Receipt") ? entry.merchant : "",
  );
  const [typeId, setTypeId] = useState(entry?.expenseType ?? "");
  const [currency, setCurrency] = useState(entry?.currency ?? "USD");
  // A refund is saved as a negative amount; here it's typed as it's printed, with Refund ticked.
  const [refund, setRefund] = useState(
    (entry?.amountUsd ?? 0) < 0 && entry?.deductFromPaycheck !== true,
  );
  // Owed from their paycheck: the company paid the bill, so the line pays back minus the personal part.
  const [deduct, setDeduct] = useState(entry?.deductFromPaycheck === true);
  const showDeduct = canDeduct || entry?.deductFromPaycheck === true;
  // The personal part is taken off the receipt's total (`amount`); the box shows what's left.
  const [personal, setPersonal] = useState(
    entry?.personalAmount ? String(entry.personalAmount) : "",
  );
  const [amount, setAmount] = useState(() => {
    if (entry?.deductFromPaycheck) return "";
    const value = Math.abs((foreign ? entry.originalAmount : entry?.amountUsd) ?? 0);
    return value > 0
      ? (Math.round((value + (entry?.personalAmount ?? 0)) * 100) / 100).toString()
      : "";
  });
  // The Reimbursement Amount as it's being typed, kept until the box is left.
  const [claimTyped, setClaimTyped] = useState<string>();
  // Typed by hand, the USD amount stays as typed; otherwise it follows the rate.
  const [usdTyped, setUsdTyped] = useState(
    foreign && entry.amountUsd !== 0 ? String(Math.abs(entry.amountUsd)) : undefined,
  );
  const [date, setDate] = useState(entry?.incurredOn ?? todayIso());
  const [notes, setNotes] = useState(entry?.notes ?? "");
  const [attendees, setAttendees] = useState(entry?.attendees ?? "");
  const [headcount, setHeadcount] = useState(entry?.headcount ? String(entry.headcount) : "");
  const [days, setDays] = useState(entry?.days ? String(entry.days) : "");
  const [clientName, setClientName] = useState(entry?.clientName ?? "");
  const [brand, setBrand] = useState(entry?.brand ?? "");
  const [answer, setAnswer] = useState(entry?.fieldAnswer ?? "");
  const [tip, setTip] = useState(entry?.tipAmount ? String(entry.tipAmount) : "");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState<File>();
  const [progress, setProgress] = useState<number>();
  const canAttach = !onPendingSaved && reportId !== undefined;

  const chooseReceipt = (file: File) => {
    setReceipt(file);
    setError(undefined);
  };
  const { dragging, dropProps } = useReceiptInput(canAttach, chooseReceipt, setError);

  const type = findType(catalog, typeId)?.type;
  const rate = catalog?.currencies.find((c) => c.code === currency);
  const personalPart = refund ? 0 : Number(personal) || 0;
  const deducting = deduct && showDeduct && !refund;
  // What's paid back, in the expense's currency: the receipt's total less the personal part.
  const claimed = Math.round((Number(amount) - personalPart) * 100) / 100;
  const converted =
    currency !== "USD" && rate && claimed > 0
      ? (Math.round(claimed * rate.usdPerUnit * 100) / 100).toFixed(2)
      : "";
  const usd = currency === "USD" ? String(claimed) : (usdTyped ?? converted);
  const askPeople =
    type !== undefined && (type.field === "Attendees" || type.perPersonLimit !== "Off");
  const askTip = type !== undefined && (type.isMeal || type.tip !== "Off");
  // A daily limit is checked per day, so a receipt for several days (parking) says how many.
  const askDays =
    type !== undefined && !type.isMeal && type.dailyLimit !== "Off" && type.dailyLimitUsd !== null;

  async function attach(report: ReportDetail): Promise<ReportDetail> {
    if (!receipt || !reportId) return report;
    const id = entry?.id ?? report.entries.find((e) => !knownEntryIds.includes(e.id))?.id;
    if (!id) return report;
    const { photo, sections } = await prepareReceipt(receipt);
    const form = new FormData();
    form.set("file", photo);
    for (const section of sections) form.append("section", section);
    setProgress(0);
    return api.uploadForm<ReportDetail>(
      `/api/expenses/reports/${reportId}/entries/${id}/receipt`,
      form,
      setProgress,
    );
  }

  const text = (value: string) => (value.trim() === "" ? null : value.trim());

  return (
    <form
      className={`stack ${dragging ? "drop-target" : ""}`}
      {...dropProps}
      onSubmit={(event) => {
        event.preventDefault();
        if (deducting && personalPart <= 0) {
          setError("Enter the personal amount to deduct from the paycheck.");
          return;
        }
        if (!deducting && personalPart > 0 && claimed <= 0) {
          setError("The personal part can't be the whole receipt.");
          return;
        }
        setBusy(true);
        setError(undefined);
        const sign = refund ? -1 : 1;
        const body = {
          incurredOn: date,
          merchant,
          category: type ? null : (entry?.category ?? null),
          expenseType: typeId === "" ? null : typeId,
          // The receipt's own itemised description stays; a typed expense has none.
          description: entry?.description ?? null,
          // Deducted, the server works the amount out from the personal part.
          amountUsd: deducting ? -personalPart : sign * (Number(usd) || 0),
          currency,
          originalAmount: currency === "USD" || deducting ? null : sign * claimed,
          personalAmount: personalPart > 0 ? personalPart : null,
          deductFromPaycheck: deducting,
          notes: text(notes),
          attendees: type?.field === "Attendees" ? text(attendees) : null,
          headcount: askPeople && Number(headcount) > 0 ? Math.round(Number(headcount)) : null,
          days: askDays && Number(days) > 1 ? Math.round(Number(days)) : null,
          clientName: type?.field === "ClientName" ? text(clientName) : null,
          brand: type?.field === "Brand" ? text(brand) : null,
          fieldAnswer: type?.field === "Other" ? text(answer) : null,
          tipAmount: askTip && !refund && Number(tip) > 0 ? Number(tip) : null,
        };
        const saving: Promise<unknown> =
          entry && onPendingSaved
            ? api
                .patch<ExpenseEntry>(`/api/expenses/pending/${entry.id}`, body)
                .then(onPendingSaved)
            : (entry
                ? api.patch<ReportDetail>(
                    `/api/expenses/reports/${reportId ?? ""}/entries/${entry.id}`,
                    body,
                  )
                : api.post<ReportDetail>(
                    `/api/expenses/reports/${reportId ?? ""}/entries/manual`,
                    body,
                  )
              ).then((report) =>
                attach(report).then(
                  (withReceipt) => onSaved?.(withReceipt),
                  (caught: unknown) => {
                    // The expense itself is saved; only the receipt failed.
                    void dialogs
                      .notify({
                        title: "The receipt didn't upload",
                        message: `The expense was saved, but not its receipt: ${message(caught)}`,
                      })
                      .then(() => onSaved?.(report));
                  },
                ),
              );
        saving.catch((caught: unknown) => {
          setError(message(caught));
          setBusy(false);
        });
      }}
    >
      <label>
        Merchant
        <input
          value={merchant}
          onChange={(e) => setMerchant(e.target.value)}
          required
          placeholder="Where was it?"
          autoComplete="off"
        />
      </label>
      <div className="type-field">
        <label>
          Expense type
          <select value={typeId} onChange={(e) => setTypeId(e.target.value)} required>
            <option value="" disabled>
              {catalog ? "Choose a type" : "Loading…"}
            </option>
            {catalog?.categories
              .filter(
                (c) =>
                  (c.active && (!c.availableTo?.length || usable?.has(c.id) === true)) ||
                  c.types.some((t) => t.id === typeId),
              )
              .map((category) => (
                <optgroup key={category.id} label={category.name}>
                  {category.types
                    .filter((t) => t.active || t.id === typeId)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </optgroup>
              ))}
          </select>
        </label>
        {/* Money back, taken off the report. */}
        <label className="check type-refund">
          <input type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)} />
          <span>Refund</span>
        </label>
      </div>
      {type && <TypeRules type={type} />}
      <TypeNotice type={type} />
      {!refund && (
        <label>
          <span>
            Personal Expense{" "}
            <span className="optional">
              - if any portion of this expense is personal, enter it here
            </span>
          </span>
          <input
            value={personal}
            onChange={(e) => setPersonal(e.target.value)}
            inputMode="decimal"
            pattern="[0-9]+(\.[0-9]{1,2})?"
            title="An amount such as 18.40"
            placeholder="0.00"
          />
        </label>
      )}
      {!refund && showDeduct && (
        <label className="check">
          <input type="checkbox" checked={deduct} onChange={(e) => setDeduct(e.target.checked)} />
          <span>Deduct from Paycheck</span>
        </label>
      )}
      <label>
        {refund ? "Amount refunded" : "Reimbursement Amount"}
        <span className="input-currency">
          <input
            value={
              deducting
                ? personalPart > 0
                  ? (-personalPart).toFixed(2)
                  : ""
                : (claimTyped ?? (personalPart === 0 || amount === "" ? amount : String(claimed)))
            }
            readOnly={deducting}
            onChange={(e) => {
              // Typed here, it's what's paid back: the receipt's total is it plus the personal part.
              const typed = e.target.value;
              setClaimTyped(typed);
              setAmount(
                typed === "" || personalPart === 0
                  ? typed
                  : String(Math.round((Number(typed) + personalPart) * 100) / 100),
              );
            }}
            onBlur={() => setClaimTyped(undefined)}
            inputMode="decimal"
            pattern="[0-9]+(\.[0-9]{1,2})?"
            title="An amount such as 18.40"
            placeholder="0.00"
            required={!deducting}
          />
          <select
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value);
              setUsdTyped(undefined);
            }}
            aria-label="Currency"
          >
            <option value="USD">USD</option>
            {catalog?.currencies.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
              </option>
            ))}
            {currency !== "USD" && !rate && <option value={currency}>{currency}</option>}
          </select>
        </span>
        {deducting && personalPart > 0 && (
          <span className="hint">
            {money(personalPart, currency)} in personal expense is deducted from the paycheck. The
            company paid the bill.
          </span>
        )}
        {!deducting && personalPart > 0 && claimed > 0 && (
          <span className="hint">
            {money(personalPart, currency)} in personal expense is deducted from{" "}
            {money(Number(amount), currency)} Receipt total
          </span>
        )}
      </label>
      {currency !== "USD" && !deducting && (
        <label>
          {personalPart > 0 ? "Paid back in US dollars" : "In US dollars"}
          <span className="input-prefix">
            <span aria-hidden="true">$</span>
            <input
              value={usd}
              onChange={(e) => setUsdTyped(e.target.value)}
              inputMode="decimal"
              pattern="[0-9]+(\.[0-9]{1,2})?"
              placeholder="0.00"
              required
            />
          </span>
          <span className="hint">
            {rate
              ? `At 1 ${currency} = ${money(rate.usdPerUnit)}${usdTyped !== undefined ? ", corrected by hand" : ""}.`
              : `${currency} has no rate in Settings; enter the dollar amount.`}
          </span>
        </label>
      )}
      <label>
        Date
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
      </label>
      {type?.field === "Attendees" && (
        <label>
          Who else was there <FieldNeed mode={type.fieldRequired} />
          <input
            value={attendees}
            onChange={(e) => setAttendees(e.target.value)}
            placeholder="Names, separated by commas"
          />
        </label>
      )}
      {askPeople && (
        <label>
          People, including you <span className="optional">optional</span>
          <input
            value={headcount}
            onChange={(e) => setHeadcount(e.target.value)}
            inputMode="numeric"
            pattern="[0-9]+"
            placeholder={
              attendees.trim()
                ? String(attendees.split(",").filter((n) => n.trim()).length + 1)
                : "1"
            }
          />
        </label>
      )}
      {askDays && (
        <label>
          Days it covers <span className="optional">optional</span>
          <input
            value={days}
            onChange={(e) => setDays(e.target.value)}
            inputMode="numeric"
            pattern="[0-9]+"
            placeholder="1"
          />
          <span className="hint">
            For a receipt covering several days, such as airport parking. The daily limit is checked
            per day.
          </span>
        </label>
      )}
      {type?.field === "ClientName" && (
        <label>
          Client or vendor <FieldNeed mode={type.fieldRequired} />
          <input
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Who you met with"
          />
        </label>
      )}
      {type?.field === "Brand" && (
        <label>
          Brand <FieldNeed mode={type.fieldRequired} />
          <select value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="">Choose a brand</option>
            {catalog?.brands.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </label>
      )}
      {type?.field === "Other" && (
        <label>
          {type.fieldPrompt ?? "Details"} <FieldNeed mode={type.fieldRequired} />
          <input value={answer} onChange={(e) => setAnswer(e.target.value)} />
        </label>
      )}
      {askTip && !refund && (
        <label>
          Tip <span className="optional">included in the amount</span>
          <input
            value={tip}
            onChange={(e) => setTip(e.target.value)}
            inputMode="decimal"
            pattern="[0-9]+(\.[0-9]{1,2})?"
            placeholder="0.00"
          />
        </label>
      )}
      <label>
        {type && type.explanation !== "Off" ? "Explanation" : "Note"}{" "}
        <FieldNeed mode={type?.explanation ?? "Off"} />
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={
            type && type.explanation !== "Off" ? "Why was it needed?" : "What it was for"
          }
        />
      </label>
      {canAttach && (
        <div className="attach">
          <label className="btn secondary small">
            <Icon name="camera" size={16} />
            {receipt
              ? "Change receipt"
              : entry?.receipt
                ? "Replace the receipt"
                : "Attach a receipt"}
            <input
              className="visually-hidden"
              type="file"
              accept={ReceiptAccept}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) setReceipt(file);
              }}
            />
          </label>
          {!receipt && <PasteButton onFile={chooseReceipt} onRefused={setError} />}
          {receipt ? (
            <span className="attach-name">
              {receipt.name}
              <button
                type="button"
                className="link-btn"
                onClick={() => setReceipt(undefined)}
                aria-label="Don't attach this receipt"
              >
                <Icon name="x" size={14} />
              </button>
            </span>
          ) : (
            <span className="muted small">
              {entry?.receipt ? "A receipt is attached." : "Optional."}
            </span>
          )}
          <p className="attach-hint muted small">
            A photo, PDF or email. You can also paste a receipt or drag it here.
          </p>
        </div>
      )}
      {progress !== undefined && (
        <div className="upload" role="status">
          <span>{progress < 1 ? "Uploading receipt…" : "Reading receipt…"}</span>
          <progress value={progress < 1 ? progress : undefined} max={1} />
        </div>
      )}
      <FormError text={error} />
      <div className="sheet-actions">
        <button className="btn" disabled={busy}>
          {busy ? "Saving…" : entry ? "Save changes" : "Add expense"}
        </button>
      </div>
    </form>
  );
}

/** "required" or "optional" next to a field, from the rule's mode. */
function FieldNeed({ mode }: { mode: RuleMode }) {
  return <span className="optional">{mode === "Off" ? "optional" : "required"}</span>;
}

/** The limits on a type in one line, so they're known before the receipt is. */
function TypeRules({ type }: { type: ExpenseTypeDefinition }) {
  const rules: string[] = [];
  if (type.dailyLimit !== "Off" && type.dailyLimitUsd !== null)
    rules.push(`up to ${money(type.dailyLimitUsd)} a day, tax and tip included`);
  if (type.perPersonLimit !== "Off" && type.perPersonLimitUsd !== null)
    rules.push(`up to ${money(type.perPersonLimitUsd)} a person`);
  if (type.perDay !== "Off" && type.maxPerDay !== null)
    rules.push(`${String(type.maxPerDay)} a day`);
  if (type.tip !== "Off" && type.maxTipPercent !== null)
    rules.push(`tip up to ${String(type.maxTipPercent)}%`);
  if (type.explanation !== "Off") rules.push("needs an explanation");
  if (type.receipt !== "Off")
    rules.push(
      type.receiptAboveUsd > 0 ? `receipt from ${money(type.receiptAboveUsd)}` : "receipt required",
    );
  if (rules.length === 0) return null;
  const sentence = rules.join(", ");
  return (
    <p className="type-rules">
      <Icon name="alert" size={14} /> {sentence.charAt(0).toUpperCase() + sentence.slice(1)}.
    </p>
  );
}

type Point = { lat: number; lng: number };

/** A trip's saved point, if it has one. */
function pointOf(latitude: number | null | undefined, longitude: number | null | undefined) {
  return latitude != null && longitude != null ? { lat: latitude, lng: longitude } : undefined;
}

/**
 * Adds a mileage trip, or changes one when `entry` is set. A changed trip is measured again and
 * keeps the rate it was added at.
 */
export function MileageForm({
  reportId,
  rate: currentRate,
  typedMilesAllowed = true,
  notesRequired = false,
  entry,
  onSaved,
}: {
  reportId: string;
  rate: number;
  /** From Settings: off, the miles come only from the start and end and can't be typed. */
  typedMilesAllowed?: boolean;
  /** From Settings: every trip needs a note saying what it was for. */
  notesRequired?: boolean;
  entry?: ExpenseEntry;
  onSaved: (report: ReportDetail) => void;
}) {
  const api = useApi();
  const trip = entry?.mileage ?? undefined;
  const rate = trip?.rateUsd ?? currentRate;
  const [origin, setOrigin] = useState(trip?.originLabel ?? "");
  const [destination, setDestination] = useState(trip?.destinationLabel ?? "");
  const [originPoint, setOriginPoint] = useState<Point | undefined>(
    pointOf(trip?.originLatitude, trip?.originLongitude),
  );
  const [destPoint, setDestPoint] = useState<Point | undefined>(
    pointOf(trip?.destinationLatitude, trip?.destinationLongitude),
  );
  const [typedMiles, setTypedMiles] = useState(
    trip?.calculationMethod === "Manual" ? String(trip.distanceMiles) : "",
  );
  // An existing trip keeps the distance it was measured at until its start or end changes.
  const [measured, setMeasured] = useState<MileageQuote | undefined>(
    trip && trip.calculationMethod !== "Manual"
      ? {
          miles: trip.distanceMiles,
          calculatedMiles: trip.calculatedMiles,
          method: trip.calculationMethod,
        }
      : undefined,
  );
  const [measuring, setMeasuring] = useState(false);
  const [date, setDate] = useState(entry?.incurredOn ?? todayIso());
  const [notes, setNotes] = useState(entry?.notes ?? "");
  const [locating, setLocating] = useState<"origin" | "destination">();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const bothEnds =
    (originPoint !== undefined || origin.trim().length > 3) &&
    (destPoint !== undefined || destination.trim().length > 3);
  const quote = measured;

  /** Measures the drive when they ask, rather than on every keystroke. */
  function calculate() {
    setMeasuring(true);
    setError(undefined);
    api
      .post<MileageQuote>("/api/expenses/mileage/quote", {
        incurredOn: date,
        originLabel: origin || "Start",
        originLatitude: originPoint?.lat,
        originLongitude: originPoint?.lng,
        destinationLabel: destination || "End",
        destinationLatitude: destPoint?.lat,
        destinationLongitude: destPoint?.lng,
        manualMiles: null,
      })
      .then(setMeasured)
      .catch(() => {
        setMeasured(undefined);
        setError("We couldn't work out the distance. Check both addresses, or use your location.");
      })
      .finally(() => setMeasuring(false));
  }

  function locate(which: "origin" | "destination") {
    if (!("geolocation" in navigator)) {
      setError("This browser cannot share its location. Type the address instead.");
      return;
    }
    setLocating(which);
    setError(undefined);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const point = { lat: position.coords.latitude, lng: position.coords.longitude };
        const apply = (label: string) => {
          if (which === "origin") {
            setOriginPoint(point);
            setOrigin(label);
          } else {
            setDestPoint(point);
            setDestination(label);
          }
          setMeasured(undefined);
          setLocating(undefined);
        };
        // The street address reads far better on a report than a pair of coordinates.
        api
          .get<{ address: string | null }>(
            `/api/expenses/mileage/address?lat=${String(point.lat)}&lon=${String(point.lng)}`,
          )
          .then((result) => apply(result.address ?? "Current location"))
          .catch(() => apply("Current location"));
      },
      () => {
        setLocating(undefined);
        setError("Location access was declined. Type the address instead.");
      },
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  }

  const typed = !typedMilesAllowed || typedMiles.trim() === "" ? undefined : Number(typedMiles);
  const distance = typed ?? quote?.miles;
  const needsMiles = quote === undefined;

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setError(undefined);
        const body = {
          incurredOn: date,
          originLabel: origin,
          originLatitude: originPoint?.lat,
          originLongitude: originPoint?.lng,
          destinationLabel: destination,
          destinationLatitude: destPoint?.lat,
          destinationLongitude: destPoint?.lng,
          manualMiles: typed ?? null,
          notes: notes.trim() === "" ? null : notes.trim(),
        };
        (entry
          ? api.put<ReportDetail>(
              `/api/expenses/reports/${reportId}/entries/${entry.id}/mileage`,
              body,
            )
          : api.post<ReportDetail>(`/api/expenses/reports/${reportId}/entries/mileage`, body)
        )
          .then(onSaved)
          .catch((caught: unknown) => {
            setError(message(caught));
            setBusy(false);
          });
      }}
    >
      <p className="muted">
        Paid at {money(rate)} a mile. Enter where you started and ended, or use your location, then
        click Calculate mileage for the driving distance and a map of the route.
      </p>
      <label>
        From
        <span className="input-action">
          <input
            value={origin}
            onChange={(e) => {
              setOrigin(e.target.value);
              setOriginPoint(undefined);
              setMeasured(undefined);
            }}
            required
            placeholder="Address or place"
          />
          <button
            type="button"
            className={`icon-btn ${originPoint ? "on" : ""}`}
            onClick={() => locate("origin")}
            aria-label="Use my current location as the start"
            disabled={locating !== undefined}
          >
            <Icon name="pin" />
          </button>
        </span>
      </label>
      <label>
        To
        <span className="input-action">
          <input
            value={destination}
            onChange={(e) => {
              setDestination(e.target.value);
              setDestPoint(undefined);
              setMeasured(undefined);
            }}
            required
            placeholder="Address or place"
          />
          <button
            type="button"
            className={`icon-btn ${destPoint ? "on" : ""}`}
            onClick={() => locate("destination")}
            aria-label="Use my current location as the end"
            disabled={locating !== undefined}
          >
            <Icon name="pin" />
          </button>
        </span>
      </label>
      {locating && <p className="muted">Finding your address…</p>}
      <button
        type="button"
        className="btn secondary"
        onClick={calculate}
        disabled={!bothEnds || measuring || locating !== undefined}
      >
        <Icon name="car" size={16} /> {measuring ? "Calculating…" : "Calculate mileage"}
      </button>
      <div className="split">
        {typedMilesAllowed ? (
          <label>
            Miles {!needsMiles && <span className="optional">optional</span>}
            <input
              value={typedMiles}
              onChange={(e) => setTypedMiles(e.target.value)}
              inputMode="decimal"
              placeholder={quote ? String(quote.miles) : "0"}
              required={needsMiles}
            />
          </label>
        ) : (
          <label>
            Miles <span className="optional">from the route</span>
            <input
              value={quote ? String(quote.miles) : ""}
              readOnly
              tabIndex={-1}
              className="read-only"
              placeholder="Click Calculate mileage"
            />
          </label>
        )}
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </label>
      </div>
      <label>
        Notes {!notesRequired && <span className="optional">optional</span>}
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          required={notesRequired}
          rows={2}
          placeholder="What the trip was for, e.g. visiting a vendor"
        />
      </label>
      {distance !== undefined && !Number.isNaN(distance) && distance > 0 && (
        <div className="estimate">
          <span>
            {miles(distance)}
            <span className="estimate-how">
              {typed !== undefined
                ? " typed in"
                : quote?.method === "AzureMaps"
                  ? " by road"
                  : " in a straight line"}
            </span>
          </span>
          <strong>{money(Math.round(distance * rate * 100) / 100)}</strong>
        </div>
      )}
      <FormError text={error} />
      <div className="sheet-actions">
        <button className="btn" disabled={busy || (!typedMilesAllowed && quote === undefined)}>
          {busy ? "Saving…" : entry ? "Save trip" : "Add trip"}
        </button>
      </div>
    </form>
  );
}

export function ReceiptForm({
  reportId,
  onSaved,
}: {
  reportId: string;
  onSaved: (report: ReportDetail) => void;
}) {
  const api = useApi();
  const [progress, setProgress] = useState<number>();
  const [error, setError] = useState<string>();

  function upload(original: File, via: ReceiptVia) {
    setError(undefined);
    setProgress(0);
    prepareReceipt(original)
      .then(({ photo, sections }) => {
        const body = new FormData();
        body.set("file", photo);
        body.set("via", via);
        for (const section of sections) body.append("section", section);
        return api.uploadForm<ReportDetail>(
          `/api/expenses/reports/${reportId}/entries/receipt`,
          body,
          setProgress,
        );
      })
      .then(onSaved)
      .catch((caught: unknown) => {
        setError(message(caught));
        setProgress(undefined);
      });
  }

  const uploading = progress !== undefined;
  const { dragging, dropProps } = useReceiptInput(!uploading, upload, setError);

  return (
    <div className={`stack ${dragging ? "drop-target" : ""}`} {...dropProps}>
      {uploading ? (
        <div className="upload" role="status">
          <span>{progress < 1 ? "Uploading receipt…" : "Reading receipt…"}</span>
          <progress value={progress < 1 ? progress : undefined} max={1} />
        </div>
      ) : (
        <>
          <label className="btn big">
            <Icon name="camera" size={22} />
            Take a photo
            <input
              className="visually-hidden"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) upload(file, "Photo");
              }}
            />
          </label>
          <label className="btn secondary">
            <Icon name="file" />
            Choose a photo, PDF or email
            <input
              className="visually-hidden"
              type="file"
              accept={ReceiptAccept}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) upload(file, "Upload");
              }}
            />
          </label>
          <PasteButton
            className="btn secondary"
            onFile={(file) => upload(file, "Paste")}
            onRefused={setError}
          />
          <p className="muted small center">
            You can also paste a receipt or drag a photo, PDF or email here.
          </p>
        </>
      )}
      <FormError text={error} />
    </div>
  );
}
